import type { MemberAccess, ProjectMember } from "@cloudserver/contracts";
import { Eye, ShieldCheck, UserMinus, UserPlus, Users } from "lucide-react";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useProject } from "../api/queries.js";
import {
  useIsAdmin,
  useProjectMembers,
  useRemoveProjectMember,
  useSetProjectMember,
  useUsers,
} from "../api/users.js";
import { DangerDialog } from "../ui/confirm.js";
import { ErrorMessage } from "../ui/feedback.js";
import { EmptyState, PageHeader } from "../ui/layout.js";
import { Dialog, type MenuItem } from "../ui/overlay.js";
import { Alert, Button, Select } from "../ui/primitives.js";
import { DataTable } from "../ui/table.js";
import { AccessBadge, StatusBadge, useChangeRunner } from "./users/shared.js";
import { useStepUpGuard } from "./users/step-up.js";

/**
 * A project's "Access" tab: who may see and change this project and how.
 * Everyone with access sees the list; administrators add, change and
 * remove people (docs/users.md).
 */
export function ProjectAccessPage({ projectId }: { projectId: string }) {
  const { t } = useTranslation();
  const project = useProject(projectId);
  const members = useProjectMembers(projectId);
  const admin = useIsAdmin();
  const run = useChangeRunner();
  const { guard, dialog } = useStepUpGuard();
  const set = useSetProjectMember(projectId);
  const remove = useRemoveProjectMember(projectId);
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<ProjectMember | null>(null);
  const name = project.data?.name ?? "";

  const change = (member: ProjectMember, access: MemberAccess) =>
    void run(
      () => guard(() => set.mutateAsync({ userId: member.userId, access })),
      t("access.changed", { name: member.name }),
    );

  /** Administrators change members here; administrators themselves see every project anyway. */
  const memberMenu = (m: ProjectMember): MenuItem[][] =>
    m.access === "admin"
      ? [
          [
            {
              label: t("access.adminRow"),
              icon: <ShieldCheck className="size-4" aria-hidden />,
              disabled: true,
              reason: t("access.adminRowReason"),
              onSelect: () => undefined,
            },
          ],
        ]
      : [
          [
            m.access === "guest"
              ? {
                  label: t("access.makeOwner"),
                  icon: <ShieldCheck className="size-4" aria-hidden />,
                  onSelect: () => change(m, "owner"),
                }
              : {
                  label: t("access.makeGuest"),
                  icon: <Eye className="size-4" aria-hidden />,
                  onSelect: () => change(m, "guest"),
                },
          ],
          [
            {
              label: t("access.remove"),
              icon: <UserMinus className="size-4" aria-hidden />,
              danger: true,
              onSelect: () => setRemoving(m),
            },
          ],
        ];

  const addButton = admin ? (
    <Button onClick={() => setAdding(true)} icon={<UserPlus className="size-4" aria-hidden />}>
      {t("access.add")}
    </Button>
  ) : undefined;

  return (
    <>
      <PageHeader
        title={t("access.title")}
        intro={t("access.intro", { name })}
        help="users"
        actions={addButton}
      />
      {!admin && <Alert tone="neutral">{t("access.readOnly")}</Alert>}
      {members.error ? (
        <ErrorMessage error={members.error} />
      ) : (
        <DataTable
          label={t("access.title")}
          rows={members.data}
          loading={members.isPending}
          rowKey={(m) => m.userId}
          initialSort={{ key: "name", dir: "asc" }}
          empty={
            <EmptyState
              icon={<Users className="size-6" aria-hidden />}
              title={t("access.empty")}
              action={addButton}
              help="users"
            >
              {t("access.emptyLead")}
            </EmptyState>
          }
          columns={[
            {
              key: "name",
              header: t("users.name"),
              primary: true,
              sort: (m) => m.name.toLowerCase(),
              cell: (m) => (
                <span className="flex flex-col">
                  <span className="font-medium">{m.name}</span>
                  <span className="text-xs text-muted">{m.email}</span>
                </span>
              ),
            },
            {
              key: "access",
              header: t("access.accessColumn"),
              sort: (m) => m.access,
              cell: (m) => <AccessBadge access={m.access} />,
            },
            {
              key: "status",
              header: t("users.statusLabel"),
              cell: (m) => <StatusBadge status={m.status} />,
            },
          ]}
          {...(admin ? { rowMenu: memberMenu } : {})}
        />
      )}
      {adding && (
        <AddMemberDialog
          projectId={projectId}
          present={new Set((members.data ?? []).map((m) => m.userId))}
          onClose={() => setAdding(false)}
          guard={guard}
        />
      )}
      {removing && (
        <DangerDialog
          open
          onOpenChange={(open) => !open && setRemoving(null)}
          title={t("access.removeTitle", { name: removing.name, project: name })}
          consequences={[t("access.removeHidden"), t("access.removeKeep")]}
          actionLabel={t("access.remove")}
          busy={remove.isPending}
          onConfirm={() => {
            const member = removing;
            setRemoving(null);
            void run(
              () => guard(() => remove.mutateAsync(member.userId)),
              t("access.removed", { name: member.name }),
            );
          }}
        />
      )}
      {dialog}
    </>
  );
}

/** Gives a member of the organisation access to this project. */
function AddMemberDialog({
  projectId,
  present,
  onClose,
  guard,
}: {
  projectId: string;
  present: Set<string>;
  onClose: () => void;
  guard: <T>(change: () => Promise<T>) => Promise<T>;
}) {
  const { t } = useTranslation();
  const users = useUsers();
  const set = useSetProjectMember(projectId);
  const run = useChangeRunner();
  // Administrators see every project anyway; only members can be added.
  const candidates = (users.data ?? []).filter((u) => u.role === "member" && !present.has(u.id));
  const [userId, setUserId] = useState("");
  const [access, setAccess] = useState<MemberAccess>("guest");
  const chosen = userId || candidates[0]?.id || "";

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const person = candidates.find((u) => u.id === chosen);
    if (!person) return;
    void run(
      () => guard(() => set.mutateAsync({ userId: person.id, access })),
      t("access.added", { name: person.name }),
    ).then((done) => done && onClose());
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t("access.add")}
      description={t("access.addLead")}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            type="submit"
            form="add-member"
            busy={set.isPending}
            disabled={candidates.length === 0}
            icon={<UserPlus className="size-4" aria-hidden />}
          >
            {t("access.addSubmit")}
          </Button>
        </>
      }
    >
      {candidates.length === 0 && !users.isPending ? (
        <p className="text-sm text-muted">{t("access.nobodyToAdd")}</p>
      ) : (
        <form id="add-member" onSubmit={submit} className="flex flex-col gap-4">
          <label className="flex flex-col gap-1.5 text-sm font-medium">
            {t("access.person")}
            <Select
              label={t("access.person")}
              value={chosen}
              onChange={(e) => setUserId(e.target.value)}
            >
              {candidates.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name} ({u.email})
                </option>
              ))}
            </Select>
          </label>
          <label className="flex flex-col gap-1.5 text-sm font-medium">
            {t("access.accessColumn")}
            <Select
              label={t("access.accessColumn")}
              value={access}
              onChange={(e) => setAccess(e.target.value as MemberAccess)}
            >
              <option value="owner">{t("users.access.owner")}</option>
              <option value="guest">{t("users.access.guest")}</option>
            </Select>
          </label>
          <p className="text-xs text-muted">{t(`access.meaning.${access}`)}</p>
        </form>
      )}
    </Dialog>
  );
}
